import { QueryClient, UseMutationResult } from 'react-query';
import HttpServiceHelper from './httpServiceHelper';
import { _HttpMethods, _Payload, _Permission, _PermissionGroup } from '../constants/staticTypes';
import { getVenuePermissionGroupListApi, getVenuePermissionGroupOptionsApi, getVenuePermissionListApi } from '../constants/apiEndPoints';
import { AxiosResponse } from 'axios';
import { getErrorMessage } from '../utils/common';
import { AlertVariant } from '../constants/constants';
import { _UpdatePermissionForm } from './permissionHelper';

// slim, non-paginated group list for a venue picker - data.lists of _PermissionGroupOption
export const getVenuePermissionGroupOptions = (queryClient: QueryClient, xTenantId: string) =>
    HttpServiceHelper({ url: getVenuePermissionGroupOptionsApi, method: _HttpMethods.GET, queryClient, xTenantId });

export const getVenuePermissionList = (queryClient: QueryClient, xTenantId?: string, limit = 500) => {
    const url = `${getVenuePermissionListApi}?limit=${limit}`;
    return HttpServiceHelper({
        url,
        method: _HttpMethods.GET,
        queryClient,
        xTenantId,
    });
};

export const getVenuePermissionGroupList = (queryClient: QueryClient, xTenantId?: string, limit = 500) => {
    const url = `${getVenuePermissionGroupListApi}?limit=${limit}`;
    return HttpServiceHelper({
        url,
        method: _HttpMethods.GET,
        queryClient,
        xTenantId,
    });
};

export const getVenuePermissionWithGroupList = async (queryClient: QueryClient, xTenantId?: string) => {
    const [venuePermissionListResponse, venuePermissionGroupListResponse] = await Promise.all([
        getVenuePermissionList(queryClient, xTenantId),
        getVenuePermissionGroupList(queryClient, xTenantId),
    ]);
    return {
        venuePermissionListData: venuePermissionListResponse?.data?.data as _Permission[],
        venuePermissionGroupListData: venuePermissionGroupListResponse?.data?.data?.lists as _PermissionGroup[],
    };
};

export const onVenuePermissionChange = (
    currentPermission: _Permission,
    activePermissions: _Permission[],
    isActive: boolean
): _Permission[] => {
    return activePermissions.map((permission) => {
        // Check if the current permission is the updated permission
        if (permission.id === currentPermission.id) {
            permission.isActive = isActive;

            // If the updatedPermission is a parent, update all its children
            if (permission.subPermissions && permission.subPermissions.length > 0) {
                permission.subPermissions = permission.subPermissions.map((child) => {
                    child.isActive = isActive;
                    return child;
                });
            }
        }

        // Recursively update subPermissions
        if (permission.subPermissions && permission.subPermissions.length > 0) {
            permission.subPermissions = onVenuePermissionChange(currentPermission, permission.subPermissions, isActive);

            // Determine parent isActive based on children
            const allChildrenInactive = permission.subPermissions.every((sub) => !sub.isActive);

            if (allChildrenInactive) {
                permission.isActive = false;
            } else {
                const hasActiveChild = permission.subPermissions.some((sub) => sub.isActive);
                permission.isActive = permission.isActive || hasActiveChild;
            }
        }

        return permission;
    });
};

export type _AddPermissionForm = {
    permissionRoleName: string;
    permissionRoleDescription: string;
};

export const addVenuePermission = (
    addVenuePermissionMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _AddPermissionForm,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void,
    xTenantId?: string
) => {
    addVenuePermissionMutation.mutate(
        {
            url: getVenuePermissionGroupListApi,
            method: _HttpMethods.POST,
            data: param,
            xTenantId,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const deleteVenuePermission = (
    deleteVenuePermissionMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: { id: string },
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void,
    xTenantId?: string
) => {
    deleteVenuePermissionMutation.mutate(
        {
            url: getVenuePermissionGroupListApi,
            method: _HttpMethods.DELETE,
            data: param,
            xTenantId,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const updateVenuePermission = (
    updateVenuePermissionMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _UpdatePermissionForm,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void,
    xTenantId?: string
) => {
    updateVenuePermissionMutation.mutate(
        {
            url: getVenuePermissionGroupListApi,
            method: _HttpMethods.PUT,
            data: param,
            xTenantId,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};
